'use client'

import { useMemo, useState } from 'react'

import { ArrowUpIcon, CheckIcon, Loader2Icon, PlugZapIcon } from 'lucide-react'

import { MetaOAuthPreflightDialog } from '@/components/meta-oauth-preflight-dialog'
import { SampleReportArtifact } from '@/components/marketing/product-ui/sample-report-artifact'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  peekFreeAdsAuditRunIntent,
  useFreeAdsAuditConnect,
  type FreeAdsAuditPlatform,
} from '@/hooks/use-free-ads-audit-connect'
import { useFreeAdsAuditStart } from '@/hooks/use-free-ads-audit-start'
import { track } from '@/lib/analytics/track'
import { GoogleIcon, MetaIcon } from '@/lib/integrations'
import { cn } from '@/lib/utils'

import { FREE_ADS_AUDIT_PATH } from './free-ads-audit-url'

function auditErrorMessage(code: string): string {
  if (code === 'already_used') {
    return 'You have already used your free audit. Its report is in your Soku chat history.'
  }
  if (code === 'global_limit') {
    return 'The free audit is at capacity right now — please try again shortly.'
  }
  return 'Something went wrong starting the audit. Please try again.'
}

function ConnectButton({
  icon,
  label,
  loading,
  disabled,
  highlighted,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  // `loading` shows a spinner (only the platform being connected); `disabled`
  // just greys the button out (the other platform while one is in flight).
  loading?: boolean
  disabled?: boolean
  highlighted?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'bg-background text-foreground flex min-h-14 w-full items-center justify-center gap-2.5 rounded-full px-5 text-base font-medium shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60',
        highlighted && 'ring-primary ring-2 ring-offset-2 ring-offset-black/40',
      )}
    >
      {loading ? <Loader2Icon className="size-5 animate-spin" /> : icon}
      {label}
    </button>
  )
}

// Hero badge + headline + subcopy: centered on small screens, left-aligned
// beside the sample artifact on large ones. Mirrors Free Competitor Analysis.
function AuditHeroCopy() {
  return (
    <>
      <div className="mb-6 inline-flex items-center gap-2 rounded-md border border-white/50 px-3 py-1 text-xs text-white/90 sm:text-sm">
        <PlugZapIcon className="size-3.5" />
        Free ads account audit
      </div>
      <h1 className="mx-auto max-w-2xl font-sans text-4xl leading-tight !font-normal tracking-tight sm:text-5xl md:text-6xl lg:mx-0">
        Find the wasted spend hiding in{' '}
        <span className="text-primary font-serif italic">your ad accounts</span>
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-lg text-white/80 md:text-xl lg:mx-0">
        Soku connects to Meta and Google Ads and audits your account to reveal
        what changed, why it changed, and what to do next — in a shareable report
        you can act on today.
      </p>
    </>
  )
}

/**
 * The interactive audit hero: copy + connect controls beside a sample report.
 * Once an ad account is connected, submitting the website opens the audit as
 * the first message of the brand's chat (`useFreeAdsAuditStart`); the report
 * is written and delivered there, not on this page.
 */
export function AuditHeroExperience() {
  const { isLoaded, isSignedIn, startSignUp, user } = useAuthContext()
  const {
    connect,
    isConnecting,
    connectingPlatform,
    error: connectError,
  } = useFreeAdsAuditConnect()
  const { start, starting, error: startError } = useFreeAdsAuditStart()
  const [websiteUrl, setWebsiteUrl] = useState('')
  // Set when the visitor submits the website pill before connecting an ad
  // account — the audit can't run without a connection, so we nudge them to the
  // Connect buttons rather than no-op.
  const [needsConnect, setNeedsConnect] = useState(false)
  // The platform the visitor connected (exactly one — by design there is no
  // second connect). When set, we show the "enter your website + press Enter"
  // ready screen instead of auto-running. Derived (not lazy useState) because the
  // run intent is scoped to the signed-in user, and `user` populates a tick after
  // mount — a one-shot initializer would read it before auth loaded and miss the
  // legit return. A memo (not an effect) keeps this off React 19's
  // no-setState-in-effect path; `peek` is a pure read and the render below gates
  // on `isLoaded`, so there's no SSR/hydration mismatch.
  const readyRun = useMemo<{
    brandId: string
    platform: FreeAdsAuditPlatform
  } | null>(() => {
    if (!isLoaded) return null
    const intent = peekFreeAdsAuditRunIntent(user?.id ?? null)
    return intent ? { brandId: intent.brandId, platform: intent.platform } : null
  }, [isLoaded, user?.id])

  const [metaPreflightOpen, setMetaPreflightOpen] = useState(false)

  function handleConnect(platform: FreeAdsAuditPlatform) {
    track(
      'marketing_cta_click',
      { cta_location: 'hero', cta_text: `Connect ${platform}`, signed_in: isSignedIn },
    )
    setNeedsConnect(false)
    if (!isSignedIn) {
      // startSignUp wraps its argument in `/sign-up?redirect_url=…`, so pass the
      // bare destination path — passing a pre-built sign-up URL would double-wrap
      // and bounce the verified user back to /sign-up instead of the audit page.
      startSignUp(FREE_ADS_AUDIT_PATH)
      return
    }
    // Meta's consent flow asks which business portfolios to share, which reads
    // as a hard requirement to anyone without a Business Manager — and this
    // landing page is a first-touch entry point, so that is most visitors here.
    // Same notice as the connect dialog and onboarding; this path redirects on
    // its own (`useFreeAdsAuditConnect`), so it needs its own gate.
    if (platform === 'meta') {
      setMetaPreflightOpen(true)
      return
    }
    void connect(platform, websiteUrl.trim() || undefined)
  }

  // The website pill mirrors FCA's submit affordance, but the audit can't run
  // from a URL alone — it needs a connected ad account. So submitting just
  // surfaces the "connect first" nudge; the typed URL still rides along as audit
  // context once the visitor connects below.
  function handleSubmitWebsite(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setNeedsConnect(true)
  }

  // Post-connect: the visitor enters their website and presses Enter to open
  // the single audit for the platform they connected in the brand's chat.
  function handleRunSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!readyRun) return
    void start({
      brandId: readyRun.brandId,
      platform: readyRun.platform,
      websiteUrl: websiteUrl.trim() || undefined,
    })
  }

  // 2-column hero (copy + controls on the left, sample report on the right) —
  // mirrors the Free Competitor Analysis idle hero.
  return (
    <div className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 items-center gap-12 px-5 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16">
      <div className="text-center text-white lg:text-left">
        <AuditHeroCopy />
        <div className="mx-auto mt-10 w-full max-w-xl lg:mx-0">
          {!isLoaded ? (
            <div className="h-40" aria-hidden />
          ) : isSignedIn && readyRun ? (
            <ReadyToRun
              platform={readyRun.platform}
              websiteUrl={websiteUrl}
              onWebsiteChange={setWebsiteUrl}
              onSubmit={handleRunSubmit}
              starting={starting}
              errorCode={startError ?? connectError}
            />
          ) : (
            <ConnectPanel
              websiteUrl={websiteUrl}
              onWebsiteChange={setWebsiteUrl}
              onSubmitWebsite={handleSubmitWebsite}
              isConnecting={isConnecting}
              connectingPlatform={connectingPlatform}
              needsConnect={needsConnect}
              onConnect={handleConnect}
              errorCode={startError ?? connectError}
            />
          )}
        </div>
      </div>

      <SampleReportArtifact className="mx-auto w-full max-w-md lg:mx-0 lg:justify-self-end" />

      <MetaOAuthPreflightDialog
        open={metaPreflightOpen}
        onOpenChange={setMetaPreflightOpen}
        onConfirm={() => {
          setMetaPreflightOpen(false)
          void connect('meta', websiteUrl.trim() || undefined)
        }}
      />
    </div>
  )
}

// Connected, awaiting run: the visitor connected one platform; they now enter
// their website and press Enter to open the single audit in their chat. No
// auto-run, no second connect — kept deliberately simple.
function ReadyToRun({
  platform,
  websiteUrl,
  onWebsiteChange,
  onSubmit,
  starting,
  errorCode,
}: {
  platform: FreeAdsAuditPlatform
  websiteUrl: string
  onWebsiteChange: (value: string) => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  starting: boolean
  errorCode: string | null
}) {
  const platformLabel = platform === 'meta' ? 'Meta Ads' : 'Google Ads'
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white">
        <CheckIcon className="size-4" />
        {platform === 'meta' ? <MetaIcon size="sm" /> : <GoogleIcon size="sm" />}
        {platformLabel} connected
      </div>
      <form onSubmit={onSubmit} noValidate>
        <div className="border-input bg-background text-foreground flex min-h-14 w-full items-center gap-3 rounded-full border px-5 shadow-sm">
          <input
            aria-label="Product website"
            className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none"
            inputMode="url"
            autoCapitalize="none"
            autoComplete="url"
            autoCorrect="off"
            autoFocus
            placeholder="https://yourproduct.com"
            value={websiteUrl}
            onChange={event => onWebsiteChange(event.target.value)}
            disabled={starting}
          />
          <button
            type="submit"
            disabled={starting}
            className="bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Run free ads audit"
          >
            {starting ? (
              <Loader2Icon className="size-5 animate-spin" />
            ) : (
              <ArrowUpIcon className="size-5" />
            )}
          </button>
        </div>
      </form>
      {errorCode ? (
        <p className="text-sm text-rose-200">{auditErrorMessage(errorCode)}</p>
      ) : (
        <p className="text-sm text-white/80">
          {starting
            ? 'Opening your audit in Soku…'
            : `Enter your website and press Enter — your ${platformLabel} audit runs in your Soku chat.`}
        </p>
      )}
    </div>
  )
}

// Idle (signed in or out): website pill + the two platform connect buttons, then
// the trust row — mirrors the Free Competitor Analysis idle hero.
function ConnectPanel({
  websiteUrl,
  onWebsiteChange,
  onSubmitWebsite,
  isConnecting,
  connectingPlatform,
  needsConnect,
  onConnect,
  errorCode,
}: {
  websiteUrl: string
  onWebsiteChange: (value: string) => void
  onSubmitWebsite: (event: React.FormEvent<HTMLFormElement>) => void
  isConnecting: boolean
  connectingPlatform: FreeAdsAuditPlatform | null
  needsConnect: boolean
  onConnect: (platform: FreeAdsAuditPlatform) => void
  errorCode: string | null
}) {
  return (
    <div className="flex w-full flex-col gap-3">
      {/* Same pill as Free Competitor Analysis — `text-foreground` is explicit
          because the hero wraps content in `text-white`. */}
      <form onSubmit={onSubmitWebsite} noValidate>
        <div className="border-input bg-background text-foreground flex min-h-14 w-full items-center gap-3 rounded-full border px-5 shadow-sm">
          <input
            aria-label="Product website"
            className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-base outline-none"
            inputMode="url"
            autoCapitalize="none"
            autoComplete="url"
            autoCorrect="off"
            placeholder="https://yourproduct.com"
            value={websiteUrl}
            onChange={event => onWebsiteChange(event.target.value)}
            disabled={isConnecting}
          />
          <button
            type="submit"
            disabled={isConnecting}
            className="bg-primary text-primary-foreground flex size-10 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Run free ads audit"
          >
            <ArrowUpIcon className="size-5" />
          </button>
        </div>
      </form>

      {needsConnect && (
        <p className="text-sm text-white/90">
          Connect your Meta or Google Ads account below to run the audit.
        </p>
      )}

      <ConnectButton
        icon={<MetaIcon size="sm" />}
        label="Connect Meta Ads"
        loading={connectingPlatform === 'meta'}
        disabled={isConnecting}
        highlighted={needsConnect}
        onClick={() => onConnect('meta')}
      />
      <ConnectButton
        icon={<GoogleIcon size="sm" />}
        label="Connect Google Ads"
        loading={connectingPlatform === 'google'}
        disabled={isConnecting}
        highlighted={needsConnect}
        onClick={() => onConnect('google')}
      />

      {errorCode ? (
        <p className="text-sm text-rose-200">{auditErrorMessage(errorCode)}</p>
      ) : (
        <p className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-white/80 lg:justify-start">
          <span className="inline-flex items-center gap-1.5">
            <CheckIcon className="size-4" /> Read-only
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckIcon className="size-4" /> Free during beta
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckIcon className="size-4" /> No credit card
          </span>
        </p>
      )}
    </div>
  )
}
